// Core components
import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { isArray } from "util";

// Third party components
import { MatDialog } from "@angular/material";
import { FieldType } from "@ngx-formly/material";
import { TranslateService } from "@ngx-translate/core";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import { FileSystemFileEntry, NgxFileDropEntry } from "ngx-file-drop";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import { finalize } from "rxjs/operators";

// Custom components
import { ConfigService } from 'src/app/shared/services/config.service';
import { FileService } from "src/app/shared/services/file.service";
import { HelperService } from "src/app/shared/services/helper.service";
import { LightboxDialogComponent } from "../../dialogs/lightbox-dialog/lightbox-dialog.component";

@Component({
  selector: "app-formly-file-uploader-type",
  templateUrl: "./file-uploader-type.component.html",
  styleUrls: ["./file-uploader-type.component.scss"]
})
export class FileUploaderTypeComponent extends FieldType
  implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  triggerFileUpload = false;
  triggerFileReset = false;
  importFormSubmitBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.import"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    customClass: "mt-2"
  };
  uploadedFiles: string[] = [];
  formField;
  formControl;
  @Input() customLabel;
  fileIsUploading = false;
  /**
   * Uploads started and not ended yet. A file dropped while another upload is running starts a
   * second one, so a flag would end with the first: the count ends with the last. Only the page
   * that asks hears of it, @see notifyUploading
   */
  private uploadsInFlight = 0;

  constructor(
    private helperService: HelperService,
    private translateService: TranslateService,
    private toastrService: ToastrService,
    private fileService: FileService,
    private configService: ConfigService,
    public dialog: MatDialog
  ) {
    super();
  }

  /**
   * Init component
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    super.ngOnInit();

    if (this.formControl.value) {
      this.updateUpdatedFiles(this.formControl.value, false);
    }

    // Handle form values changes
    // this.subscriptions.push(
    //   this.formControl.valueChanges.subscribe(res => {
    //   })
    // );

    if (typeof this.to.allowedFilesNumber === "undefined") {
      this.to.allowedFilesNumber = 1;
    }

    if (typeof this.to.allowedFilesExtensions === "undefined") {
      this.to.allowedFilesExtensions = "*";
    }

    this.importFormSubmitBtnOptions.disabled = this.options.formState.disabled;
  }

  /**
   * After view init logic
   *
   * @since 1.0.0
   */
  ngAfterViewInit(): void {
    super.ngAfterViewInit();
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.uploadedFiles = [];
    // An upload still reading its files has no subscription yet: nothing else would end it
    if (this.uploadsInFlight > 0) {
      this.uploadsInFlight = 0;
      this.notifyUploading(false);
    }
  }

  /**
   * Handle event "uploadFiles" emitted from file-uploader component
   *
   * @since 1.0.0
   *
   * @param files Files to upload
   * @returns undefined
   */
  onUploadFiles(files: NgxFileDropEntry[]): void {
    if (this.options.formState.disabled) {
      console.warn('Block upload because field is disabled') //@todo: translate
      return;
    } else {
      this.importFormSubmitBtnOptions.disabled = false;
    }
    this.importFormSubmitBtnOptions.active = true;
    this.fileIsUploading = true;
    this.uploadStarted();

    // You could upload it like this:
    const formData = new FormData();

    let counter = 1;
    for (const givenFile of files) {
      const fileEntry = givenFile.fileEntry as FileSystemFileEntry;
      fileEntry.file((file: File) => {
        // const name = `file-${counter}`;
        const name = `file[]`;
        formData.append(name, file, givenFile.relativePath);
        counter++;
        if (counter === files.length + 1) {
          const uploadFilesSubscription = this.fileService
            .uploadImportingFiles(formData, this.to.endpoint)
            .pipe(
              finalize(() => {
                this.importFormSubmitBtnOptions.active = false;
                // Every end passes here: answer, refusal, error, or the field destroyed
                this.uploadEnded();
              })
            )
            .subscribe(
              res => {
                // Accept all-and-only last (this.to.allowedFilesNumber)
                // files (this is need! do not remove!)
                if (res.status) {
                  let files = [];
                  if (this.to.endpoint && this.to.endpoint === 'supplier') {
                    files = res.data.slice(-this.to.allowedFilesNumber);
                  } else {
                    files = res.files.slice(-this.to.allowedFilesNumber);
                  }
                  res.files = files;
                }

                // If available, call the callback
                if (typeof this.to.callback === "function") {
                  this.to.callback(res, this.model);
                }

                // Handle res status
                if (res.status) {
                  this.triggerFileReset = !this.triggerFileReset; // Reset drop-zone area
                  this.updateUpdatedFiles(res.files, true);
                  this.fileIsUploading = false;
                  return;
                }
                if (res.message) {
                  this.toastrService.error(
                    this.translateService.instant("common.words.warning"),
                    res.message
                  );
                  this.fileIsUploading = false;
                  return;
                }
                this.fileIsUploading = false;
                this.toastrService.error(
                  this.translateService.instant("common.errors.unknown")
                );
              },
              err => {
                // Choose one of the following error handling
                // method. The first one show a message right
                // under the form fields (if the form is properly
                // setted), the second one show toastr
                // notifications for each error
                // this.helperService.handleFormError(this.form, err);
                this.helperService.handleError(err);
                this.fileIsUploading = false;
              }
            );
          this.subscriptions.push(uploadFilesSubscription);
        }
      });
    }
  }

  /**
   * An upload starts: the page that asked is told on the first one only.
   *
   * @since 3.0.0
   */
  private uploadStarted(): void {
    this.uploadsInFlight++;
    if (this.uploadsInFlight === 1) {
      this.notifyUploading(true);
    }
  }

  /**
   * An upload ended, however it ended. The page that asked is told when the last one ends, after
   * the answer has put the files in the field.
   *
   * @since 3.0.0
   */
  private uploadEnded(): void {
    // An upload that outlived its field: the destroy already said it was over
    if (this.uploadsInFlight === 0) {
      return;
    }
    this.uploadsInFlight--;
    if (this.uploadsInFlight === 0) {
      this.notifyUploading(false);
    }
  }

  /**
   * Tell the page whether a file is still on its way, when it asked with
   * `templateOptions.uploadingChange`. The option is only read, never written: the pages that do
   * not pass it, and the form configs stored elsewhere, see the uploader exactly as before.
   *
   * @since 3.0.0
   */
  private notifyUploading(uploading: boolean): void {
    if (typeof this.to.uploadingChange === "function") {
      this.to.uploadingChange(uploading);
    }
  }

  /**
   * Absolute URL of an uploaded file for the "open in new tab" link.
   * updateUpdatedFiles() already prefixes the API host to file.path, so the host
   * must not be prepended again; Windows-style separators are normalised.
   *
   * @since 3.0.0
   */
  getFileUrl(file: any): string {
    const rawPath = file && typeof file.path === 'string' ? file.path.replace(/\\/g, '/') : '';
    if (/^https?:\/\//i.test(rawPath)) {
      return rawPath;
    }
    const apiHost = this.to.endpoint && this.to.endpoint === 'supplier'
      ? this.configService.apiSuppliersHost
      : this.configService.apiHost;
    return `${apiHost}/${rawPath.replace(/^\/+/, '')}`;
  }

  /**
   * Update current updatedFiles
   *
   * @since 1.0.0
   */
  updateUpdatedFiles(files: any[], setValue = false): void {
    // let apiHost = environment.apiProtocol + environment.apiHost;
    // if (environment.apiPort !== "" && environment.apiHost !== "80") {
    //   apiHost += `:${environment.apiPort}`;
    // }
    let apiHost = this.configService.apiHost;
    if (this.to.endpoint && this.to.endpoint === 'supplier') {
      apiHost = this.configService.apiSuppliersHost;
    }

    // Update image previews list
    if (files) {
      files.forEach((file: any) => {
        const path = file.path;
        // if (path.startsWith("public")) {
        if (path.substring(0,apiHost.length) !== apiHost) {
          const newPath = `${apiHost}\\${path}`;
          file.path = newPath;
        }
        // }
        this.uploadedFiles.push(file);
      });

      // Prevent visualization of more previews than allowed
      this.uploadedFiles = this.uploadedFiles.slice(-this.to.allowedFilesNumber);

      if (setValue) {
        this.value = this.uploadedFiles;
      }
    }
  }

  /**
   * Remove given item from:
   * 1) File previews
   * 2) Field's value
   *
   * @since 1.0.0
   */
  onRemove(file: any): void {

    // Remove file from previews
    this.uploadedFiles = this.uploadedFiles.filter(
      (item: any) => item._id !== file._id
    );

    const key = this.key as string
    // Remove file from field value
    if (typeof file._id !== "undefined" && this.model[key] === file._id) {
      this.model[key] = null;
    }
    if (isArray(this.model[key])) {
      this.model[key] = this.model[key].filter(
        (item: any) => item._id !== file._id
      );
    }
  }

  /**
   * Handle click on a image preview and opens it in a dialog
   *
   * @since 1.0.0
   */
  onOpenImage(file: any): void {
    this.dialog.open(LightboxDialogComponent, {
      data: {
        url: file.path
      },
      panelClass: "lightbox-dialog"
    });
  }

  /**
   * Transform given size in bytes to
   * a kb string
   *
   * @since 1.0.0
   *
   * @param size The size to transform
   * @return Transformed size string with 2 decimals
   */
  getFormattedSize(size: number): string {
    const sizeInKb = size / 1024;
    return sizeInKb.toFixed(2);
  }
}
